'use client';

import React, { createContext, useContext, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import * as Dialog from '@radix-ui/react-dialog';
import styled from 'styled-components';
import {
  BodyTextMediumB,
  BodyTextRegularC,
  HeadingMediumH5,
} from '@/shared/ui/typography2';
import { useScopedI18n } from '@locales/client';

type NavigationGuardContextType = {
  needsConfirmation: boolean;
  setNeedsConfirmation: (val: boolean) => void;
  requestNavigation: (url: string) => void;
};

const NavigationGuardContext = createContext<NavigationGuardContextType | null>(
  null
);

export function NavigationGuardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const needsConfirmationRef = useRef(false);
  const setNeedsConfirmation = (value: boolean) => {
    needsConfirmationRef.current = value;
  };
  const needsConfirmation = (): boolean => {
    return needsConfirmationRef.current;
  };
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const t = useScopedI18n('common.game.dockbar');

  const requestNavigation = (url: string) => {
    if (needsConfirmationRef.current) {
      setPendingUrl(url);
      setDialogOpen(true);
    } else {
      router.push(url);
    }
  };

  const confirmNavigation = () => {
    if (pendingUrl) router.push(pendingUrl);
    setDialogOpen(false);
    setPendingUrl(null);
  };

  const cancelNavigation = () => {
    setDialogOpen(false);
    setPendingUrl(null);
  };

  return (
    <NavigationGuardContext.Provider
      value={{
        needsConfirmation,
        setNeedsConfirmation,
        requestNavigation,
      }}
    >
      {children}

      <Dialog.Root open={dialogOpen} onOpenChange={setDialogOpen}>
        <Dialog.Portal>
          <DialogOverlay />
          <DialogContent>
            <TextContainer>
              <DialogTitle>{t('exit_request_title')}</DialogTitle>
              <DialogDescription color="#AFAFAF">
                {t('exit_request_description')}
              </DialogDescription>
            </TextContainer>
            <Buttons>
              <ExitButton onClick={confirmNavigation}>
                <BodyTextMediumB>{t('exit_request_confirm')}</BodyTextMediumB>
              </ExitButton>
              <Dialog.Close asChild>
                <CancelButton onClick={cancelNavigation}>
                  <BodyTextMediumB>{t('exit_request_cancel')}</BodyTextMediumB>
                </CancelButton>
              </Dialog.Close>
            </Buttons>
          </DialogContent>
        </Dialog.Portal>
      </Dialog.Root>
    </NavigationGuardContext.Provider>
  );
}

export function useNavigationGuard() {
  const context = useContext(NavigationGuardContext);
  const router = useRouter();

  return (
    context ?? {
      needsConfirmation: false,
      setNeedsConfirmation: () => {},
      requestNavigation: (url: string) => router.push(url),
    }
  );
}

const DialogOverlay = styled(Dialog.Overlay)`
  position: fixed;
  inset: 0;
  animation: overlayShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
`;
const DialogContent = styled(Dialog.Content)`
  display: flex;
  width: 300px;

  padding: 24px 18px 18px 18px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;

  border-radius: 12px;
  background-color: rgba(19, 19, 21, 0.75);
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(24px);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: contentShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
  @media (orientation: landscape) {
    height: 183px;
  }
  @media (orientation: portrait) {
    width: 254px;
  }
`;
const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const DialogTitle = styled(Dialog.Title)`
  ${HeadingMediumH5};
`;
const DialogDescription = styled(Dialog.Description)`
  ${BodyTextRegularC};
  color: #afafaf;
  text-align: center;
`;
const Buttons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  width: 100%;
  @media (orientation: portrait) {
    flex-direction: column;
  }
`;
const DialogButton = styled.button`
  display: flex;
  height: 48px;
  min-width: 120px;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
  border-radius: 8px;
  @media (orientation: portrait) {
    flex: none;
    width: 100%;
  }
`;
const ExitButton = styled(DialogButton)`
  //padding: 0 24px;
  gap: 12px;
  background: rgba(208, 51, 49, 0.5);
`;
const CancelButton = styled(DialogButton)`
  //padding: 0 16px;
  gap: 8px;
  background: #625a63;
`;
